/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The row's smart-charger mark, flipped (D3, D22): a smart charger being
 * required is the NORM, so it earns nothing; the mark appears only where the
 * answer is not the norm.
 *
 *   required === true   nothing at all (the cell stays empty — D22)
 *   required === false  the PlugZap icon + "Ook zonder slimme laadpaal", emerald,
 *                       on two lines. Self-naming, so no sr prefix; its judgement
 *                       is spelled out for assistive tech ("(gunstig)").
 *   required === null   muted "Niet gecontroleerd" (an absence, never a warning)
 *                       with the sr-only prefix "Slimme laadpaal: " — on the
 *                       ten-column row the header names the column, the screen
 *                       reader does not see it
 *
 * Two layouts, one decision (src/lib/termsPresentation.ts `termsSmartMark`):
 *   row   (default)  the xl cell and the line of five: 11 px on 14 px lines, the
 *                    icon only on the green mark
 *   card             the phone/tablet card, under the VOORWAARDEN label: 10 px,
 *                    two lines, the icon on both marks; unknown reads "Slimme
 *                    lader: niet gecontroleerd" (`cardKey`) because a bare
 *                    "Niet gecontroleerd" under that label read as "terms not
 *                    checked" (Phase 0)
 * Use: `<SmartMark required={provider.smartChargingRequired} />` (the phone card:
 * `variant="card"`).
 *
 * `text-balance` lets the two lines come out as "Ook zonder / slimme laadpaal"
 * in a cell too narrow for one.
 */

import React from 'react';
import { PlugZap } from 'lucide-react';
import { cn } from '../../lib/cn';
import { termsSmartMark } from '../../lib/termsPresentation';
import { useTermsText } from './termsText';
import { TONE_TEXT } from './toneText';

interface SmartMarkProps {
  /** The provider's `smartChargingRequired`: true = required, false = optional, null = not checked. */
  required: boolean | null | undefined;
  /** `row` = the collapsed row's cell; `card` = under the label of the phone/tablet card. */
  variant?: 'row' | 'card';
  className?: string;
}

export const SmartMark: React.FC<SmartMarkProps> = ({ required, variant = 'row', className }) => {
  const { t, toneWord } = useTermsText();
  const mark = termsSmartMark(required);
  if (!mark) return null;

  const card = variant === 'card';
  const optional = required === false;
  const word = toneWord(mark.tone);
  const label = t(card ? mark.cardKey : mark.key);

  return (
    <span
      className={cn(
        'flex min-w-0',
        card ? 'mt-0.5 items-start gap-1' : 'items-center gap-1',
        TONE_TEXT[mark.tone],
        className
      )}
    >
      {(optional || card) && (
        <PlugZap className={cn('shrink-0', card ? 'h-3 w-3' : 'h-3.5 w-3.5')} aria-hidden="true" />
      )}
      <span
        className={cn(
          'min-w-0 text-balance',
          card ? 'text-[10px] leading-[11px]' : 'text-[11px] leading-[14px]',
          optional ? 'font-bold' : 'font-semibold'
        )}
      >
        {/* The unknown mark under the row's own header: name the question for a screen reader. */}
        {!optional && !card && <span className="sr-only">{`${t('offer_terms.q.smart')}: `}</span>}
        {label}
        {word && <span className="sr-only">{` (${word})`}</span>}
      </span>
    </span>
  );
};
